"use client";

import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Trash2 } from "lucide-react";
import type { InvoiceData } from "@/lib/invoice-types";

interface InvoiceFormProps {
  data: InvoiceData;
  onChange: (data: InvoiceData) => void;
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
}: {
  label: string;
  value: string | number;
  onChange: (val: string) => void;
  type?: string;
  placeholder?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-8 text-sm"
      />
    </div>
  );
}

export default function InvoiceForm({ data, onChange }: InvoiceFormProps) {
  const [draft, setDraft] = useState(data);
  const draftRef = useRef(data);

  useEffect(() => {
    setDraft(data);
    draftRef.current = data;
  }, [data]);

  function commitDraft() {
    onChange(draftRef.current);
  }

  function update(patch: Partial<InvoiceData>) {
    setDraft((prev) => {
      const next = { ...prev, ...patch };
      draftRef.current = next;
      return next;
    });
  }

  function updateItem(id: string, patch: Partial<InvoiceData["items"][0]>) {
    setDraft((prev) => {
      const next = {
        ...prev,
        items: prev.items.map((item) =>
          item.id === id ? { ...item, ...patch } : item
        ),
      };
      draftRef.current = next;
      return next;
    });
  }

  function addItem() {
    const next = {
      ...draft,
      items: [
        ...draft.items,
        { id: crypto.randomUUID(), description: "", quantity: 1, unitPrice: 0 },
      ],
    };
    setDraft(next);
    draftRef.current = next;
    onChange(next);
  }

  function removeItem(id: string) {
    if (draft.items.length <= 1) return;
    const next = {
      ...draft,
      items: draft.items.filter((item) => item.id !== id),
    };
    setDraft(next);
    draftRef.current = next;
    onChange(next);
  }

  return (
    <div className="space-y-5" onBlurCapture={commitDraft}>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Fakturadetaljer</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Field
            label="Fakturanummer"
            value={draft.invoiceNumber}
            onChange={(v) => update({ invoiceNumber: v })}
            placeholder="INV-001"
          />
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Fakturadatum"
              value={draft.invoiceDate}
              onChange={(v) => update({ invoiceDate: v })}
              type="date"
            />
            <Field
              label="Förfallodatum"
              value={draft.dueDate}
              onChange={(v) => update({ dueDate: v })}
              type="date"
            />
          </div>
          <Field
            label="Moms (%)"
            value={draft.taxRate}
            onChange={(v) => update({ taxRate: parseFloat(v) || 0 })}
            type="number"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Från (avsändare)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Field
            label="Företagsnamn"
            value={draft.fromCompany}
            onChange={(v) => update({ fromCompany: v })}
            placeholder="Ditt Företag AB"
          />
          <Field
            label="Org nr"
            value={draft.fromOrgNumber}
            onChange={(v) => update({ fromOrgNumber: v })}
            placeholder="556000-0000"
          />
          <Field
            label="Adress"
            value={draft.fromAddress}
            onChange={(v) => update({ fromAddress: v })}
            placeholder="Storgatan 1"
          />
          <Field
            label="Stad / Postnummer"
            value={draft.fromCity}
            onChange={(v) => update({ fromCity: v })}
            placeholder="111 22 Stockholm"
          />
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="E-post"
              value={draft.fromEmail}
              onChange={(v) => update({ fromEmail: v })}
              type="email"
              placeholder="faktura@foretag.se"
            />
            <Field
              label="Telefon"
              value={draft.fromPhone}
              onChange={(v) => update({ fromPhone: v })}
              type="tel"
              placeholder="070-123 45 67"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Till (mottagare)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Field
            label="Företagsnamn"
            value={draft.toCompany}
            onChange={(v) => update({ toCompany: v })}
            placeholder="Kundföretag AB"
          />
          <Field
            label="Adress"
            value={draft.toAddress}
            onChange={(v) => update({ toAddress: v })}
            placeholder="Kungsgatan 5"
          />
          <Field
            label="Stad / Postnummer"
            value={draft.toCity}
            onChange={(v) => update({ toCity: v })}
            placeholder="222 33 Göteborg"
          />
          <Field
            label="E-post"
            value={draft.toEmail}
            onChange={(v) => update({ toEmail: v })}
            type="email"
            placeholder="info@kund.se"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-medium">Orderrader</CardTitle>
          <Button variant="ghost" size="icon-xs" onClick={addItem}>
            <Plus className="size-3.5" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {draft.items.map((item, i) => (
            <div key={item.id}>
              {i > 0 && <Separator className="mb-3" />}
              <div className="flex items-start gap-2">
                <div className="flex-1 space-y-2">
                  <Field
                    label="Beskrivning"
                    value={item.description}
                    onChange={(v) => updateItem(item.id, { description: v })}
                    placeholder="Tjänst eller produkt"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <Field
                      label="Antal"
                      value={item.quantity}
                      onChange={(v) =>
                        updateItem(item.id, { quantity: parseFloat(v) || 0 })
                      }
                      type="number"
                    />
                    <Field
                      label="Pris (SEK)"
                      value={item.unitPrice}
                      onChange={(v) =>
                        updateItem(item.id, { unitPrice: parseFloat(v) || 0 })
                      }
                      type="number"
                    />
                  </div>
                </div>
                {draft.items.length > 1 && (
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    className="mt-6 text-muted-foreground hover:text-destructive"
                    onClick={() => removeItem(item.id)}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Anteckningar</CardTitle>
        </CardHeader>
        <CardContent>
          <textarea
            value={draft.notes}
            onChange={(e) => update({ notes: e.target.value })}
            placeholder="Betalningsvillkor, bankuppgifter, etc."
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-h-[80px] resize-y"
          />
        </CardContent>
      </Card>
    </div>
  );
}
